import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Send, Loader2 } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import IntakePanel from "@/components/servicerequest/IntakePanel";
import { readRequestContext } from "@/lib/requestContext";
import { IS_ES } from "@/lib/i18n/lang";
import { translateText } from "@/lib/i18n/translator";
import { QuestionField, isVisible, SR_STYLES } from "@/components/servicerequest/QuestionField";
import AttachmentsStep from "@/components/servicerequest/AttachmentsStep";
import WhereWhenStep from "@/components/servicerequest/WhereWhenStep";
import BudgetStep from "@/components/servicerequest/BudgetStep";
import ReviewStep from "@/components/servicerequest/ReviewStep";
import CompletenessMeter from "@/components/servicerequest/CompletenessMeter";

const SAVE_KEYS = ["title", "description", "answers", "attachments", "address_snapshot", "urgency", "preferred_start_date", "deadline", "budget_type", "budget_min", "budget_max", "budget_hourly", "current_step", "require_insurance", "require_background_check", "time_window"];

function useAutosave(req, setReq) {
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const timer = useRef(null);
  const pending = useRef({});
  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    const body = pending.current;
    pending.current = {};
    if (!req?.id || !Object.keys(body).length) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/service-requests/${req.id}`, body);
      setReq((r) => ({ ...r, completeness: data.completeness }));
      setSavedAt(Date.now());
    } catch (err) {
      toast.error(err?.response?.data?.error || "Autosave failed");
    } finally {
      setSaving(false);
    }
  }, [req?.id, setReq]);
  const queue = (patch) => {
    pending.current = { ...pending.current, ...patch };
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  };
  return { saving, savedAt, queue, flush };
}

export default function RequestServicePage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  useDocumentTitle("Request a service");
  const [req, setReq] = useState(null);
  const [invited, setInvited] = useState(null);
  const [prefill, setPrefill] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [autoTitle, setAutoTitle] = useState(null);
  const rawTitle = req?.title_edited ? null : req?.title;
  useEffect(() => {
    if (!IS_ES || !rawTitle) return;
    let alive = true;
    translateText(rawTitle).then((t) => alive && setAutoTitle(t));
    return () => { alive = false; };
  }, [rawTitle]);
  const shownTitle = IS_ES && !titleTouched && !req?.title_edited && autoTitle ? autoTitle : req?.title || "";

  const [submitting, setSubmitting] = useState(false);
  const { saving, savedAt, queue, flush } = useAutosave(req, setReq);
  const invite = sp.get("invite");
  const started = useRef(false);
  const ctx = React.useMemo(() => readRequestContext(sp), [sp]);

  useEffect(() => {
    if (invite) api.get(`/providers/${invite}`).then((r) => setInvited(r.data)).catch(() => {});
    const from = sp.get("fromService");
    if (from) api.get(`/services/${from}`).then((r) => setPrefill(`I'd like a quote for: ${r.data?.service?.title || r.data?.title || ""}`)).catch(() => {});
    if (sp.get("q") && !sp.get("cat")) setPrefill(sp.get("q"));
  }, [invite, sp]);
  useEffect(() => {
    if (id || !user || started.current || !(ctx.category_id || ctx.subcategory_id)) return;
    started.current = true;
    createDraft({ intake_text: ctx.search_text || "", category_id: ctx.category_id, subcategory_id: ctx.subcategory_id || null, title: ctx.search_text ? ctx.search_text.slice(0, 80) : undefined });
  }, [id, user, ctx]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!id || !user) return;
    api.get(`/service-requests/${id}`).then(({ data }) => {
      if (data.request.status !== "draft") navigate(`/dashboard/service-requests/${id}`, { replace: true });
      else setReq(data.request);
    }).catch(() => { toast.error("Request not found"); navigate("/request-a-service"); });
  }, [id, user, navigate]);

  const createDraft = async (payload) => {
    try {
      const { data } = await api.post("/service-requests", { ...payload, invited_provider_ids: invited ? [invited.id] : [], context: { ...ctx, request_source: ctx.request_source || (invited ? "provider_invite" : "request_page") } });
      setReq(data);
      navigate(`/request-a-service/${data.id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not start your request");
    }
  };

  if (!id || !req) {
    return (
      <PublicLayout>
        <style>{SR_STYLES}</style>
        <div className="mx-auto max-w-4xl px-6 py-14 md:py-20" data-testid="request-service-page">
          {id || ((ctx.category_id || ctx.subcategory_id) && user) ? <div className="text-[#252A34]/60 flex items-center gap-2" data-testid="request-starting"><Loader2 className="h-4 w-4 animate-spin" /> {id ? "Loading your request…" : "Starting your request…"}</div>
            : <IntakePanel initialText={prefill} invitedName={invited?.business_name || invited?.display_name} onConfirm={createDraft} />}
        </div>
      </PublicLayout>
    );
  }

  const sections = req.questionnaire_snapshot?.sections || [];
  const steps = [...sections.map((s) => s.title), "Photos & files", req.kind === "local" ? "Where & when" : "Timeline", "Budget", "Review"];
  const step = Math.min(req.current_step || 0, steps.length - 1);
  const answers = req.answers || {};
  const set = (k, v) => { setReq((r) => ({ ...r, [k]: v })); if (SAVE_KEYS.includes(k)) queue({ [k]: v }); };
  const setAnswer = (qid, v) => set("answers", { ...answers, [qid]: v });
  const go = (n) => { set("current_step", n); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const sectionMissing = (i) => (sections[i]?.questions || []).filter((q) => q.required && isVisible(q, answers) && (answers[q.id] == null || answers[q.id] === "" || (Array.isArray(answers[q.id]) && !answers[q.id].length)));
  const next = () => {
    if (step < sections.length) {
      const miss = sectionMissing(step);
      if (miss.length) return toast.error(`Please answer: ${miss.map((q) => q.label).join(", ")}`);
    }
    if (steps[step] === "Where & when" && !req.address_snapshot?.city) return toast.error("Please add the job city");
    if ((steps[step] === "Where & when" || steps[step] === "Timeline") && !req.urgency) return toast.error("Please choose how soon you need this");
    go(step + 1);
  };
  const submit = async () => {
    setSubmitting(true);
    await flush();
    try {
      const { data } = await api.post(`/service-requests/${req.id}/submit`);
      toast.success(`Sent to ${data.matched} matched pro${data.matched === 1 ? "" : "s"}`);
      navigate(`/dashboard/service-requests/${req.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not submit");
      setSubmitting(false);
    }
  };

  const sec = sections[step];
  const body = sec ? (
    <div className="space-y-7" data-testid={`section-step-${step}`}>
      <div className="font-display text-xl font-semibold text-[#071b22]">{sec.title}</div>
      {sec.questions.filter((q) => isVisible(q, answers)).map((q) => <div key={q.id} className="sr-in"><QuestionField q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} /></div>)}
    </div>
  ) : steps[step] === "Photos & files" ? <AttachmentsStep attachments={req.attachments || []} onChange={(v) => set("attachments", v)} />
    : steps[step] === "Budget" ? <BudgetStep req={req} set={set} />
    : steps[step] === "Review" ? <ReviewStep req={req} set={set} onJump={go} />
    : <WhereWhenStep req={req} set={set} />;

  return (
    <PublicLayout>
      <style>{SR_STYLES}</style>
      <div className="mx-auto max-w-6xl px-6 py-10 md:py-14" data-testid="request-wizard">
        <button onClick={async () => { await flush(); navigate("/dashboard/requests"); }} className="text-sm text-[#165a71] font-medium inline-flex items-center gap-2 mb-5" data-testid="wizard-exit"><ArrowLeft className="h-4 w-4" /> Save & exit</button>
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">{req.questionnaire_snapshot?.name || "Service request"}</div>
        <input value={shownTitle} onChange={(e) => { setTitleTouched(true); set("title", e.target.value); }} className="mt-2 w-full bg-transparent font-display text-3xl md:text-4xl font-semibold text-[#071b22] outline-none border-b border-transparent focus:border-[#165a71]/30" data-testid="wizard-title" />
        <div className="mt-6 flex gap-1.5" data-testid="wizard-progress">
          {steps.map((s, i) => <button key={s} onClick={() => i < step && go(i)} title={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#165a71]" : "bg-slate-200"}`} data-testid={`progress-${i}`} />)}
        </div>
        <div className="mt-2 text-xs text-[#252A34]/60">Step {step + 1} of {steps.length} · {steps[step]}</div>
        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_300px] items-start">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-9">
            <div key={step} className="sr-in">{body}</div>
            <div className="mt-10 flex items-center justify-between">
              {step > 0 ? <button onClick={() => go(step - 1)} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-medium" data-testid="wizard-back">Back</button> : <span />}
              {step < steps.length - 1 ? (
                <button onClick={next} className="rounded-full bg-[#165a71] text-white px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#11485a]" data-testid="wizard-next">Continue <ArrowRight className="h-4 w-4" /></button>
              ) : (
                <button onClick={submit} disabled={submitting} className="rounded-full bg-[#071b22] text-white px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 hover:bg-black disabled:opacity-60" data-testid="wizard-submit">
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Matching pros…</> : <><Send className="h-4 w-4" /> Send to matched pros</>}
                </button>
              )}
            </div>
          </div>
          <div className="lg:sticky lg:top-24 space-y-4">
            <CompletenessMeter c={req.completeness} saving={saving} savedAt={savedAt} />
            <div className="rounded-2xl bg-[#071b22] text-white p-5 text-sm">
              <div className="font-semibold">How it works</div>
              <ol className="mt-2 space-y-1.5 text-white/75 text-xs list-decimal list-inside">
                <li>We send your request to the best-matched pros</li>
                <li>They reply with estimates — usually within a day</li>
                <li>Compare and accept one to book securely</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
